import {isMapsightPending, onMapsightReady} from './mapsight-hydration';

/* Image metadata is emitted in inert templates so linked images never contain
 * nested interactive controls. Enhancement mounts each notice beside its link,
 * over the actual image rectangle, without changing the CMS image layout. */
const entries = new Set();
let frame;
let nextId = 0;
function close(entry, restoreFocus = false) {
    if (!entry.notice.open) return;
    entry.notice.open = false;
    if (restoreFocus) entry.notice.querySelector('summary').focus({preventScroll: true});
}
function fitPanel(entry) {
    if (!entry.notice.open) return;
    const panel = entry.layer.querySelector('.bs-image-notice__panel');
    panel.removeAttribute('data-overflow');
    panel.toggleAttribute('data-overflow', panel.scrollHeight > panel.clientHeight + 1);
}
function position(entry) {
    const {image, host, layer} = entry;
    if (isMapsightPending(image)) return;
    const hidden = image.closest('[aria-hidden="true"], [inert]') !== null;
    layer.inert = hidden;
    if (hidden) close(entry);
    const rect = image.getBoundingClientRect();
    const base = host.getBoundingClientRect();
    // Convert viewport coordinates to the containing block's CSS coordinates
    // (also handles transformed carousel slides and scaled containers).
    // Computed sizes preserve fractional pixels; offsetWidth/Height round them
    // and would shift the notice on responsive images with half-pixel heights.
    const style = getComputedStyle(host);
    const borderBoxSize = (axis, sides) => {
        const size = parseFloat(style[axis]);
        const extra = style.boxSizing === 'border-box' ? 0 : sides.reduce((sum, side) =>
            sum + parseFloat(style[`padding${side}`]) + parseFloat(style[`border${side}Width`]), 0);
        return size + extra;
    };
    const layoutWidth = borderBoxSize('width', ['Left', 'Right']);
    const layoutHeight = borderBoxSize('height', ['Top', 'Bottom']);
    const scaleX = layoutWidth ? base.width / layoutWidth : 1;
    const scaleY = layoutHeight ? base.height / layoutHeight : 1;
    if (layer.dataset.placement === 'below') {
        layer.style.setProperty('--bs-image-notice-width', `${host.clientWidth}px`);
        fitPanel(entry);
        return;
    }
    const stage = image.closest('.bs-intro-item');
    if (stage) {
        const header = document.querySelector('.bs-site-logo--big');
        const headerBottom = header?.getBoundingClientRect().bottom ?? rect.top;
        const safeTop = Math.max(76, (headerBottom - rect.top) / (scaleY || 1) + 12);
        const textElements = [...stage.querySelectorAll('.bs-intro-item__content h1, .bs-intro-item__content h2, .bs-intro-item__title'), ...document.querySelectorAll('.bs-site-head-content')];
        const textTops = textElements.map(el => el.getBoundingClientRect())
            .filter(box => box.height > 0 && box.top > rect.top + (safeTop + 24) * (scaleY || 1)).map(box => box.top);
        const textTop = textTops.length ? Math.min(...textTops) : rect.bottom;
        const available = Math.max(46, Math.min(180, (textTop - rect.top) / (scaleY || 1) - safeTop - 12));
        layer.style.setProperty('--bs-image-notice-stage-top', `${safeTop + available - 24}px`);
        layer.style.setProperty('--bs-image-notice-stage-height', `${available}px`);
    }
    layer.toggleAttribute('data-compact', rect.height / (scaleY || 1) < 96);
    layer.style.setProperty('--bs-image-notice-width', `${rect.width / (scaleX || 1)}px`);
    layer.style.setProperty('--bs-image-notice-height', `${rect.height / (scaleY || 1)}px`);
    Object.assign(layer.style, {
        left: `${(rect.left - base.left) / (scaleX || 1) - parseFloat(style.borderLeftWidth) + host.scrollLeft}px`,
        top: `${(rect.top - base.top) / (scaleY || 1) - parseFloat(style.borderTopWidth) + host.scrollTop}px`,
        width: `${rect.width / (scaleX || 1)}px`,
        height: `${rect.height / (scaleY || 1)}px`,
    });
    fitPanel(entry);
}
function update() {
    frame = null;
    for (const entry of entries) {
        if (!entry.image.isConnected || !entry.layer.isConnected) {
            entry.observer?.disconnect();
            entry.layer.remove();
            entries.delete(entry);
        } else {
            position(entry);
        }
    }
}
function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
}
function mount(root) {
    root.querySelectorAll('template[data-bs-image-notice]').forEach(template => {
        if (isMapsightPending(template)) return;
        const previous = template.previousElementSibling;
        const image = previous?.matches('img') ? previous : previous?.querySelector('img');
        if (!image) return;
        // Leave every ancestor link intact. The new interactive content is a
        // sibling, including when the whole teaser or stage item is linked.
        let host = image.parentElement;
        for (let parent = host; parent; parent = parent.parentElement) {
            if (parent.matches('a, button')) host = parent.parentElement;
        }
        if (host.matches('picture')) host = host.parentElement;
        const logoFrame = image.closest('.bs-logo-banner__image');
        if (logoFrame && !logoFrame.closest('a, button')) host = logoFrame.parentElement;
        // A lazy-load wrapper can have zero height. Keep absolute images tied
        // to their existing containing block instead of positioning that wrapper.
        const containingBlock = image.offsetParent;
        if (getComputedStyle(image).position === 'absolute' && containingBlock?.contains(host)) {
            host = containingBlock;
        }
        if (getComputedStyle(host).position === 'static') host.classList.add('bs-image-notice-host');
        const layer = document.createElement('div');
        layer.className = 'bs-image-notice-layer';
        if (image.closest('.bs-logo-banner, .bs-logo-banner__image, .bs-minisite-logo-banner__logo')) {
            layer.dataset.placement = 'below';
        }
        layer.append(template.content.cloneNode(true));
        const notice = layer.querySelector('details');
        const toggle = notice.querySelector('summary');
        const panel = notice.querySelector('.bs-image-notice__panel');
        panel.id = `bs-image-notice-panel-${++nextId}`;
        toggle.setAttribute('aria-controls', panel.id);
        toggle.setAttribute('aria-expanded', 'false');
        template.remove();
        host.append(layer);
        const entry = {image, host, layer, notice};
        entries.add(entry);
        notice.addEventListener('toggle', () => {
            toggle.setAttribute('aria-expanded', String(notice.open));
            if (notice.open) for (const other of entries) if (other !== entry) close(other);
            schedule();
        });
        const viewport = host.closest('.flickity-viewport');
        if (viewport) {
            // Focus scrolling must not shift Flickity's transformed track.
            const resetScroll = () => { viewport.scrollLeft = 0; viewport.scrollTop = 0; };
            notice.addEventListener('focusin', () => {
                resetScroll();
                requestAnimationFrame(resetScroll);
            });
        }
        // Prevent native pointer focus scrolling before the click is delivered.
        // Keyboard focus remains native; the click focuses without scrolling.
        notice.querySelector('summary').addEventListener('mousedown', event => event.preventDefault());
        notice.querySelector('summary').addEventListener('click', event => event.currentTarget.focus({preventScroll: true}));
        notice.addEventListener('keydown', event => {
            if (event.key === 'Escape' && notice.open) {
                event.preventDefault();
                event.stopPropagation();
                close(entry, true);
            }
        });
        // Do not initiate a carousel drag while selecting or scrolling credits.
        ['pointerdown', 'mousedown', 'touchstart', 'click'].forEach(type => {
            layer.addEventListener(type, event => event.stopPropagation());
        });
        if (typeof ResizeObserver !== 'undefined') {
            entry.observer = new ResizeObserver(schedule);
            entry.observer.observe(image);
            if (host !== image) entry.observer.observe(host);
        }
        image.addEventListener('load', schedule);
        position(entry);
    });
}
function start() {
    mount(document);
    onMapsightReady(root => { mount(root); schedule(); });
    new MutationObserver(records => {
        if (records.some(record => record.type === 'attributes' || record.addedNodes.length || record.removedNodes.length)) {
            mount(document);
            schedule();
        }
    }).observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden']});
    window.addEventListener('resize', schedule);
    document.addEventListener('click', event => {
        for (const entry of entries) if (!entry.layer.contains(event.target)) close(entry);
    });
    document.addEventListener('focusin', event => {
        for (const entry of entries) if (!entry.layer.contains(event.target)) close(entry);
    });
}
let started = false;
export function render() {
    if (started) return;
    started = true;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
    else start();
}
